import {
	Dispatch,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	SetStateAction,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { CommentFormData } from "../CommentForm/CommentForm";
import {
	EmojiPicker,
	FileSelector,
	IconButton,
	MentionInput,
	MentionInputHandle,
	MentionInputProps
} from "client/Components";
import { HubCommentSectionService } from "../HubCommentSection";
import { userMentionType } from "client/Components/MentionInput/converters";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import styles from "./CommentComposer.module.less";
import classNames from "classnames";
import { manageCommentImageSelection } from "../MobileComment/MobileCommentForm/MobileCommentForm";
import { HubCommentMediaKind, HubCommentMediaPreviewModel } from "client/Api/Hub/common";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice,
	useSetStateAction
} from "client/Hooks";
import { FormImageAttachment } from "../../FormImageAttachment";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { InsertSuggestionHandler, useInsertSuggestionHandler } from "../../MentionSuggestionBox/useInsertSuggestionHandler";
import { DropOverMentionSuggestionBox } from "../../MentionSuggestionBox/DropOverMentionSuggestionBox";
import { MentionInputState } from "client/Components/MentionInput/types";

export type CommentComposerHandle = {
	focus(): void;
};

export type CommentComposerProps = {
	onFormDataChange: (data: CommentFormData) => void;
	service: HubCommentSectionService;
	trackingId: string;
	initialMedia?: HubCommentMediaPreviewModel | null;
	options: HubCommentOptionsModel;
	formData: CommentFormData;
} & Pick<MentionInputProps, "autoFocus"
	| "trackingId"
	| "placeholder"
	| "onChange"
	| "onBlur"
	| "onFocus"
	| "value"
	| "isInvalid"
>;

export const CommentComposer = forwardRef(
	(
		props: CommentComposerProps,
		ref: Ref<CommentComposerHandle>
	): ReactElement => {
		const {
			onFormDataChange,
			service,
			trackingId,
			initialMedia,
			options,
			formData,
			...mentionInputPropRest
		} = props;

		const [hasFocus, setHasFocus] = useState<boolean>(false);
		const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const mentionInputRef: RefObject<MentionInputHandle | null> = useRef<MentionInputHandle>(null);
		const emojiButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
		const isTouch: boolean = useIsTouchDevice();

		const alertManager: AlertManager = useAlerts();
		const setFormData: Dispatch<SetStateAction<CommentFormData>> = useSetStateAction(
			formData,
			onFormDataChange
		);

		const insertSuggestionHandler: InsertSuggestionHandler = useInsertSuggestionHandler(
			service,
			setFormData
		);

		const selectImageHandler = useCallback(
			async (file: File): Promise<void> => {
				const selectedImageResult: TryResult<Base64FileUriUploadModel> = await manageCommentImageSelection(
					file,
					alertManager,
					options
				);

				if (!selectedImageResult.wasSuccessful) {
					return;
				}

				onFormDataChange({
					...formData,
					media: {
						isSet: true,
						value: {
							mediaKind: HubCommentMediaKind.Image,
							image: {
								isSet: true,
								value: selectedImageResult.result
							}
						}
					}
				});
			},
			[
				alertManager,
				options,
				formData,
				onFormDataChange
			]
		);

		const imgSrc = useMemo(
			(): string | undefined => {
				const { media } = formData;

				if (!media.isSet && isNotNil(initialMedia)) {
					return service.getCommentImageUrl(
						initialMedia.imageId
					);
				}

				if (
					media.isSet
						&& isNotNil(media.value)
						&& media.value
							.image
							.isSet
				) {
					return media
						.value
						.image
						.value
						.uri;
				}

				return undefined;
			},
			[
				formData,
				initialMedia,
				service
			]
		);

		const canSelectImage = isNil(imgSrc);

		const deleteImageHandler = useCallback(
			(): void => {
				onFormDataChange({
					...formData,
					media: { isSet: true, value: null }
				});
			},
			[onFormDataChange, formData]
		);

		const mentionInputFocusHandler = useCallback(
			(value: MentionInputState): void => {
				setHasFocus(true);
				mentionInputPropRest.onFocus?.(value);
			},
			[mentionInputPropRest]
		);

		const mentionInputBlurHandler = useCallback(
			(value: MentionInputState): void => {
				setHasFocus(false);
				mentionInputPropRest.onBlur?.(value);
			},
			[mentionInputPropRest]
		);

		const selectEmojiHandler = useCallback(
			(emojiUnicode: string): void => {
				mentionInputRef.current
					?.insert(emojiUnicode);

				setIsEmojiPickerOpen(false);

				mentionInputRef.current
					?.focus();
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): CommentComposerHandle => ({
				focus: (): void => {
					mentionInputRef.current
						?.focus();
				},
			}),
			[]
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				className={
					classNames(
						styles.root,
						{
							[styles.root_hasError]: props.isInvalid,
							[styles.root_hasFocus]: hasFocus
						}
					)
				}
				ref={containerRef}
			>
				<div className={styles.messageAndImageContainer}>
					<div>
						<MentionInput
							{...mentionInputPropRest}
							height="auto"
							initialHeight="large"
							mentionTypes={userMentionType}
							ref={mentionInputRef}
							trackingId="message"
							hasNoBackgroundColor
							onFocus={mentionInputFocusHandler}
							onBlur={mentionInputBlurHandler}
							renderSuggestionBox={(props, mentionSuggestionBoxRef) => (
								<DropOverMentionSuggestionBox
									{...props}
									ref={mentionSuggestionBoxRef}
									target={ensureNotNil(containerRef.current)}
									findUsers={service.findUsers}
								/>
							)}
							isBorderless
							onInsertSuggestion={insertSuggestionHandler}
						/>
					</div>
					{isNotNil(imgSrc) && (
						<div className={styles.imageContainer}>
							<FormImageAttachment
								src={imgSrc}
								onDelete={deleteImageHandler}
								trackingId="image"
							/>
						</div>
					)}
				</div>
				{!isTouch && (
					<IconButton
						ref={emojiButtonRef}
						icon="face-smile"
						kind="transparent-colored"
						size="large"
						shape="round"
						onClick={(): void => setIsEmojiPickerOpen(x => !x)}
						trackingId="add-emoji"
						isActive={isEmojiPickerOpen}
					/>
				)}
				<FileSelector
					onChange={selectImageHandler}
					contentTypes={options.supportedImageTypes}
				>
					{(onClick) => (
						<IconButton
							icon="image"
							kind="transparent-colored"
							size="large"
							shape="round"
							isDisabled={!canSelectImage}
							onClick={onClick}
							trackingId="upload-image-button"
						/>
					)}
				</FileSelector>
				{isEmojiPickerOpen
					&& isNotNil(emojiButtonRef.current)
					&& !isTouch
					&& (
						<EmojiPicker
							target={emojiButtonRef.current}
							onClose={(): void => setIsEmojiPickerOpen(false)}
							onSelected={selectEmojiHandler}
							trackingId={generateTrackingId(trackingId, "__emoji-picker")}
						/>
					)
				}
			</div>
		);
	}
);
CommentComposer.displayName="CommentComposer";
